import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [header, setHeader] = useState({
    label: "Get In Touch",
    title: "Start Your Transformation",
    description: "Schedule a confidential consultation to discuss how we can help your business achieve its full potential."
  });
  const [contactInfo, setContactInfo] = useState({
    phone: "(214) 673-2710",
    phone_alt: "",
    email: "info@shireadvisors.com",
    address: "516 South I-35E, Denton, TX 76205",
    business_hours: "Mon - Fri: 9:00 AM - 6:00 PM\nSat - Sun: By Appointment",
    form_disclaimer: "All information is treated as confidential and will not be shared with third parties.",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [headerRes, contactRes] = await Promise.all([
          axios.get(`${API}/content/section-headers/contact`),
          axios.get(`${API}/content/contact`),
        ]);
        if (headerRes.data) {
          setHeader(headerRes.data);
        }
        if (contactRes.data) {
          setContactInfo((prev) => ({ ...prev, ...contactRes.data }));
        }
      } catch (error) {
        console.log('Using default contact data');
      }
    };
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact/submit`, formData);
      setIsSubmitted(true);
      toast.success('Message sent successfully! We\'ll be in touch soon.');

      // Reset form after 3 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: '',
        });
      }, 3000);
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfoCards = [
    {
      icon: Phone,
      title: 'Call Us',
      details: [contactInfo.phone, contactInfo.phone_alt].filter(Boolean),
    },
    {
      icon: Mail,
      title: 'Email Us',
      details: [contactInfo.email].filter(Boolean),
    },
    {
      icon: MapPin,
      title: 'Visit Us',
      details: (contactInfo.address || '')
        .split(/\r?\n| - /)
        .map((line) => line.trim())
        .filter(Boolean),
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: (contactInfo.business_hours || '').split('\n').filter(Boolean),
    },
  ];

  return (
    <section id="contact" className="py-8 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-6">
          <span className="inline-block text-gold-600 font-semibold text-sm tracking-wider uppercase mb-2">
            {header.label}
          </span>
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-navy-900 mb-2">
            {header.title}
          </h2>
          <p className="text-base text-gray-600 leading-relaxed">
            {header.description}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Contact Info */}
          <div className="space-y-3">
            {contactInfoCards.map((item, index) => (
              <div
                key={index}
                className="panel-flair flex items-start gap-3 p-4 bg-white rounded-xl border-2 border-slate-200/70 hover:border-gold-400 overflow-hidden transition-all"
              >
                <div className="w-10 h-10 bg-navy-900 rounded-lg flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-4 h-4 text-gold-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-navy-900 mb-0.5 text-sm">{item.title}</h3>
                  {item.details.map((detail, i) => (
                    <p key={i} className="text-gray-600 text-sm leading-snug">
                      {detail}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl p-6 lg:p-8">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-navy-900 mb-4">Thank You!</h3>
                  <p className="text-gray-600">
                    Your message has been received. We'll contact you shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-navy-900 mb-1">
                        Full Name *
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 rounded-lg border-gray-200 focus:border-gold-500 focus:ring-gold-500"
                        placeholder="John Smith"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-navy-900 mb-1">
                        Email Address *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 rounded-lg border-gray-200 focus:border-gold-500 focus:ring-gold-500"
                        placeholder="john@company.com"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-navy-900 mb-1">
                        Phone Number
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-2 rounded-lg border-gray-200 focus:border-gold-500 focus:ring-gold-500"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-navy-900 mb-1">
                        Company Name
                      </label>
                      <Input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-2 rounded-lg border-gray-200 focus:border-gold-500 focus:ring-gold-500"
                        placeholder="Your Company"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-navy-900 mb-1">
                      How Can We Help? *
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="w-full px-4 py-2 rounded-lg border-gray-200 focus:border-gold-500 focus:ring-gold-500 resize-none"
                      placeholder="Tell us about your business challenges and how we might assist you..."
                    />
                  </div>

                  <p className="text-xs text-gray-500">
                    {contactInfo.form_disclaimer || 'All information is treated as confidential and will not be shared with third parties.'}
                  </p>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gold-500 hover:bg-gold-600 text-navy-900 font-semibold py-3 text-base rounded-lg transition-all duration-300 hover:shadow-lg disabled:opacity-70"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-navy-900/30 border-t-navy-900 rounded-full animate-spin" />
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <Send className="w-5 h-5" />
                        Send Message
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
